import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ChevronRight } from 'lucide-react'
import { createGeneralApi } from '../generator/tbsGeneralApi'
import { Badge } from './ui/badge'
import { cn, toStyleObject } from '@/lib/utils'

const itemCls = (isActive) => [
  'flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-left w-full transition-colors cursor-pointer',
  isActive
    ? 'bg-[hsl(var(--sidebar-accent))] text-[hsl(var(--sidebar-accent-foreground))] font-medium'
    : 'text-[hsl(var(--sidebar-foreground))] hover:bg-[hsl(var(--sidebar-accent))]/60 hover:text-white',
].join(' ')

// Items type 9/13 à n'importe quelle profondeur (récursif dans item.items) — utilisé pour
// enregistrer tous les items dans window.tbsComponents en une seule fois au niveau racine
// (SideMenu), indépendamment du montage/démontage des sous-menus flyout (voir plus bas).
function flattenMenuItems(items) {
  const result = []
  for (const item of items ?? []) {
    if (item.type !== 13 && item.type !== 9) continue
    result.push(item)
    if (item.items?.length) result.push(...flattenMenuItems(item.items))
  }
  return result
}

function MenuLevel({ items, activeId, onNavigate, badgeOverrides, onSetBadge, labelOverrides, onSetLabel }) {
  const [openId, setOpenId] = useState(null)
  const [anchor, setAnchor] = useState(null)
  const closeTimer = useRef(null)

  useEffect(() => () => clearTimeout(closeTimer.current), [])

  const open = (id, e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    clearTimeout(closeTimer.current)
    setOpenId(id)
    setAnchor(rect)
  }

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpenId(null), 150)
  }

  const cancelClose = () => clearTimeout(closeTimer.current)

  return (
    <div className="flex flex-col gap-0.5">
      {items.map(item => {
        if (item.type !== 13 && item.type !== 9) return null

        const isActive    = activeId === item.id
        const hasChildren = item.items?.length > 0

        const badgeValue = badgeOverrides[item.id] ?? item.badge
        const labelValue = labelOverrides[item.id] ?? item.label

        if (hasChildren) {
          return (
            <div
              key={item.id}
              id={item.id}
              data-tbs-root={item.id}
              className={item.class || undefined}
              style={toStyleObject(item.style)}
              onMouseEnter={e => open(item.id, e)}
              onMouseLeave={scheduleClose}
            >
              <button
                onClick={() => onNavigate(item.id, item.extraParams)}
                className={itemCls(isActive)}
              >
                {item.icon && <i className={`${item.icon} w-4 text-center shrink-0`} aria-hidden="true" />}
                <span className="flex-1">{labelValue}</span>
                <Badge value={badgeValue} />
                <ChevronRight size={12} className="shrink-0 opacity-60" />
              </button>
              {openId === item.id && anchor && createPortal(
                <div
                  // Borne la hauteur à l'espace restant sous l'ancre (jusqu'au bas du viewport, 8px
                  // de marge) + scroll interne (molette/pad, barre masquée — même traitement que le
                  // menu principal, voir CLAUDE.md) : sans ça, un sous-menu avec beaucoup d'items
                  // ouvert près du bas de l'écran déborderait du viewport sans aucun moyen d'atteindre
                  // les items masqués (élément `position: fixed`, pas de scroll de page qui s'applique).
                  style={{
                    position: 'fixed', left: anchor.right + 4, top: anchor.top, zIndex: 200,
                    maxHeight: `calc(100vh - ${anchor.top}px - 8px)`, overflowY: 'auto',
                  }}
                  className="min-w-[200px] rounded-md border border-[hsl(var(--sidebar-border))] bg-[hsl(var(--sidebar))] shadow-xl p-1 tbs-scrollbar-hidden"
                  onMouseEnter={cancelClose}
                  onMouseLeave={scheduleClose}
                >
                  <MenuLevel items={item.items} activeId={activeId} onNavigate={onNavigate} badgeOverrides={badgeOverrides} onSetBadge={onSetBadge} labelOverrides={labelOverrides} onSetLabel={onSetLabel} />
                </div>,
                document.body
              )}
            </div>
          )
        }

        return (
          <button
            key={item.id}
            id={item.id}
            data-tbs-root={item.id}
            onClick={() => onNavigate(item.id, item.extraParams)}
            className={cn(itemCls(isActive), item.class)}
            style={toStyleObject(item.style)}
          >
            {item.icon && <i className={`${item.icon} w-4 text-center shrink-0`} aria-hidden="true" />}
            <span className="flex-1">{labelValue}</span>
            <Badge value={badgeValue} />
          </button>
        )
      })}
    </div>
  )
}

export default function SideMenu({ items, activeId, onNavigate, badgeOverrides = {}, onSetBadge = () => {}, version }) {
  const [labelOverrides, setLabelOverrides] = useState({})
  const handleSetLabel = (id, label) => setLabelOverrides(prev => ({ ...prev, [id]: label }))

  // Enregistrement dans window.tbsComponents pour TOUS les items (y compris les sous-menus
  // imbriqués à n'importe quelle profondeur), fait une seule fois ici plutôt que dans chaque
  // MenuLevel : un sous-menu flyout (voir plus bas, createPortal) n'est monté que pendant le survol
  // — un enregistrement par instance de MenuLevel ferait disparaître les items d'un sous-menu de
  // tbsComponents dès sa fermeture (démontage), provoquant un TypeError sur tout accès différé
  // (tbsComponents[id] undefined). setBadge/setLabel restent fonctionnels même sous-menu fermé
  // (pilotés par le state React badgeOverrides/labelOverrides, pas par le DOM) — seules show()/
  // hide()/getCss()/... (API générale, DOM-based) restent sans effet tant que le flyout parent n'est
  // pas ouvert, le nœud DOM de l'item n'existant alors pas encore.
  useEffect(() => {
    window.tbsComponents = window.tbsComponents ?? {}
    const allItems = flattenMenuItems(items)
    const ids = []
    allItems.forEach(item => {
      ids.push(item.id)
      window.tbsComponents[item.id] = {
        type: 'menuitem',
        ...createGeneralApi(item.id, item.userData),
        setBadge: text => onSetBadge(item.id, text),
        setLabel: label => handleSetLabel(item.id, label),
      }
    })
    return () => { ids.forEach(id => delete window.tbsComponents[id]) }
  }, [items, onSetBadge])

  return (
    <div className="flex h-full w-full">
      <nav className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden tbs-scrollbar-hidden p-2">
        <MenuLevel
          items={items}
          activeId={activeId}
          onNavigate={onNavigate}
          badgeOverrides={badgeOverrides}
          onSetBadge={onSetBadge}
          labelOverrides={labelOverrides}
          onSetLabel={handleSetLabel}
        />
      </nav>
      {version && (
        <div
          className="shrink-0 w-5 bg-[hsl(var(--sidebar-accent))] text-[hsl(var(--sidebar-accent-foreground))] flex justify-center overflow-hidden"
          style={{ writingMode: 'vertical-rl' }}
          title={version}
        >
          <span className="text-[10px] font-bold tracking-wide pt-1.5 whitespace-nowrap">{version}</span>
        </div>
      )}
    </div>
  )
}
